import {
  createBrowserRouter,
  Navigate,
  Outlet,
  ScrollRestoration,
  useRouteError,
} from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { Home } from '@/app/Home';
import { SurahIndex } from '@/app/SurahIndex';
import { SurahReader } from '@/app/SurahReader';
import { NotFound } from '@/app/NotFound';
import { Login } from '@/app/Login';
import { AuthCallback } from '@/app/AuthCallback';
import { Settings } from '@/app/Settings';
import { Bookmarks } from '@/app/Bookmarks';
import { Search } from '@/app/Search';
import { Ask } from '@/app/Ask';
import { Blog } from '@/app/Blog';
import { BlogPost } from '@/app/BlogPost';
import { Privacy } from '@/app/Privacy';
import { Terms } from '@/app/Terms';
import { Contact } from '@/app/Contact';
import { Reader } from '@/app/Reader';
import { QuranArabic } from '@/app/QuranArabic';
import { Memorize } from '@/app/Memorize';
import { QariDashboard } from '@/app/QariDashboard';
import { QariOnboarding } from '@/app/QariOnboarding';
import { QariOnboarding2 } from '@/app/QariOnboarding2';
import { QariOnboarding3 } from '@/app/QariOnboarding3';
import { QariOnboarding4 } from '@/app/QariOnboarding4';
import { QariOnboarding5 } from '@/app/QariOnboarding5';
import { QariOnboardingFinal } from '@/app/QariOnboardingFinal';
import { StudentPortal } from '@/app/StudentPortal';
import { StudentPortalDashboard } from '@/app/StudentPortalDashboard';
import { Streak } from '@/app/Streak';
import { FindQari } from '@/app/FindQari';
import { RocketReaderPage } from '@/app/RocketReaderPage';
import { SeoDashboard } from '@/app/admin/SeoDashboard';
import { KidsApp } from '@/app/kids/KidsApp';
import { RequireAuth } from '@/components/auth/RequireAuth';
import { RequireAdmin } from '@/components/auth/RequireAdmin';
import { EmptyState } from '@/components/ui/EmptyState';

function RootLayout() {
  return (
    <AppShell>
      <Outlet />
      <ScrollRestoration
        getKey={(location) => {
          if (/^\/surah\/\d+(\/ayah\/\d+)?$/.test(location.pathname)) {
            return location.pathname.replace(/\/ayah\/\d+$/, '');
          }
          return location.pathname;
        }}
      />
    </AppShell>
  );
}

function RouteError() {
  const error = useRouteError() as
    | { message?: string; statusText?: string }
    | undefined;
  return (
    <AppShell>
      <EmptyState
        tone="error"
        title="Something went wrong."
        description={error?.message ?? error?.statusText ?? 'Unknown error.'}
        onRetry={() => window.location.reload()}
      />
    </AppShell>
  );
}

export const router = createBrowserRouter(
  [
    // The marketing home page is a standalone, full-bleed landing page
    // with its own dark chrome — it deliberately does not use AppShell.
    {
      path: '/',
      element: <Home />,
      errorElement: <RouteError />,
    },
    {
      path: 'kids/*',
      element: <KidsApp />,
      errorElement: <RouteError />,
    },
    {
      path: 'games/rocket-reader',
      element: <RocketReaderPage />,
      errorElement: <RouteError />,
    },
    // Every in-app route shares the AppShell layout (header, footer, nav).
    {
      element: <RootLayout />,
      errorElement: <RouteError />,
      children: [
        { path: 'surah', element: <SurahIndex /> },
        { path: 'surah/:number', element: <SurahReader /> },
        { path: 'surah/:number/ayah/:n', element: <SurahReader /> },
        { path: 'quran', element: <QuranArabic /> },

        // Reader — premium single-ayah reader (formerly Reader v2).
        { path: 'reader', element: <Reader /> },

        // Legacy redirect: anything still pointing to /reader-2 lands on /reader.
        { path: 'reader-2', element: <Navigate to="/reader" replace /> },

        // Memorize (workflow with Mishary Alafasy audio + repeat/hide/recall modes)
        { path: 'memorize', element: <Memorize /> },

        // Search (Phase 4)
        { path: 'search', element: <Search /> },

        // Ask (Phase 6)
        { path: 'ask', element: <Ask /> },

        // Content pages
        { path: 'blog', element: <Blog /> },
        { path: 'blog/:slug', element: <BlogPost /> },
        { path: 'privacy', element: <Privacy /> },
        { path: 'terms', element: <Terms /> },
        { path: 'contact', element: <Contact /> },

        // Auth
        { path: 'login', element: <Login /> },
        { path: 'auth/callback', element: <AuthCallback /> },

        // Protected
        {
          path: 'settings',
          element: (
            <RequireAuth>
              <Settings />
            </RequireAuth>
          ),
        },
        {
          path: 'bookmarks',
          element: (
            <RequireAuth>
              <Bookmarks />
            </RequireAuth>
          ),
        },

        // Qari Onboarding & Discovery (Phase 1)
        {
          path: 'qari',
          element: (
            <RequireAuth>
              <QariDashboard />
            </RequireAuth>
          ),
        },
        {
          path: 'qari/onboarding',
          element: (
            <RequireAuth>
              <QariOnboarding />
            </RequireAuth>
          ),
        },
        // Named per-step wizard routes (production-friendly: semantic,
        // deep-linkable, stable if steps are reordered).
        {
          path: 'qari/onboarding/identity',
          element: (
            <RequireAuth>
              <QariOnboarding2 />
            </RequireAuth>
          ),
        },
        {
          path: 'qari/onboarding/expertise',
          element: (
            <RequireAuth>
              <QariOnboarding3 />
            </RequireAuth>
          ),
        },
        {
          path: 'qari/onboarding/verification',
          element: (
            <RequireAuth>
              <QariOnboarding4 />
            </RequireAuth>
          ),
        },
        {
          path: 'qari/onboarding/submitted',
          element: (
            <RequireAuth>
              <QariOnboarding5 />
            </RequireAuth>
          ),
        },
        {
          path: 'qari/onboarding/complete',
          element: (
            <RequireAuth>
              <QariOnboardingFinal />
            </RequireAuth>
          ),
        },

        // Legacy redirects — keep old numeric URLs working for any saved
        // links or in-flight sessions. `replace` so the legacy URL doesn't
        // sit in browser history.
        {
          path: 'qari/onboarding-2',
          element: <Navigate to="/qari/onboarding/identity" replace />,
        },
        {
          path: 'qari/onboarding-3',
          element: <Navigate to="/qari/onboarding/expertise" replace />,
        },
        {
          path: 'qari/onboarding-4',
          element: <Navigate to="/qari/onboarding/verification" replace />,
        },
        {
          path: 'qari/onboarding-5',
          element: <Navigate to="/qari/onboarding/submitted" replace />,
        },
        {
          path: 'qari/onboarding-final',
          element: <Navigate to="/qari/onboarding/complete" replace />,
        },

        // Student Portal — auth-gated dashboard
        {
          path: 'student',
          element: (
            <RequireAuth>
              <StudentPortal />
            </RequireAuth>
          ),
        },
        {
          path: 'student-portal',
          element: (
            <RequireAuth>
              <StudentPortalDashboard />
            </RequireAuth>
          ),
        },

        // Streak — public student insights dashboard
        { path: 'streak', element: <Streak /> },

        // Find a Qari — public discovery & browse page
        { path: 'find-qari', element: <FindQari /> },

        // Admin — Technical SEO dashboard (admin allowlist only)
        {
          path: 'admin/seo',
          element: (
            <RequireAdmin>
              <SeoDashboard />
            </RequireAdmin>
          ),
        },

        { path: '*', element: <NotFound /> },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);
